import { t } from '@glory/i18n';
import { useVirtualizer } from '@tanstack/react-virtual';
import { Check } from 'lucide-react';
import {
  type KeyboardEvent,
  type ReactNode,
  useEffectEvent,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { Spinner } from '../components/spinner.tsx';
import { cn } from '../lib/cn.ts';

/** Длиннее — список виртуальный: в DOM только видимые строки. */
const VIRTUAL_FROM = 60;
const PAGE = 10;

/**
 * Следующая доступная строка от `from`. С `wrap` — на одну в сторону `step` по кругу (стрелки),
 * без — на `step` строк с упором в край (PageUp/PageDown). Нет доступных — -1.
 */
export function nextEnabledIndex(
  count: number,
  from: number,
  step: number,
  isDisabled: (index: number) => boolean,
  wrap = true,
): number {
  if (count === 0) return -1;
  const dir = step > 0 ? 1 : -1;
  if (wrap) {
    let index = from;
    for (let tried = 0; tried < count; tried += 1) {
      index = (((index + dir) % count) + count) % count;
      if (!isDisabled(index)) return index;
    }
    return -1;
  }
  const target = Math.min(Math.max(from + step, 0), count - 1);
  for (let index = target; index >= 0 && index < count; index += dir) if (!isDisabled(index)) return index;
  for (let index = target - dir; index >= 0 && index < count; index -= dir)
    if (!isDisabled(index)) return index;
  return -1;
}

export interface ListPanelProps<T> {
  readonly items: readonly T[];
  readonly getKey: (item: T) => string;
  readonly isDisabled?: (item: T) => boolean;
  readonly isSelected: (item: T) => boolean;
  readonly renderItem: (
    item: T,
    state: { readonly active: boolean; readonly selected: boolean },
  ) => ReactNode;
  readonly onSelect: (item: T) => void;
  /** Строка поиска над списком; без неё список сам принимает фокус и клавиши. */
  readonly search?: {
    readonly value: string;
    readonly onChange: (value: string) => void;
    readonly placeholder?: string;
  };
  /** С какой строки начать подсветку: выбранное значение. */
  readonly initialKey?: string | null;
  readonly multiple?: boolean;
  readonly loading?: boolean;
  /** Вместо списка: пусто, ошибка загрузки. */
  readonly message?: ReactNode;
  readonly label: string;
  /** Справа от строк — мини-карточка подсвеченного варианта (на телефоне скрыта). */
  readonly renderPreview?: (item: T) => ReactNode;
}

/**
 * Список вариантов во всплывающей панели: подсветка стрелками (Home/End, PageUp/PageDown),
 * Enter выбирает, мышь подсвечивает и выбирает. Фокус остаётся в строке поиска —
 * активная строка сообщается через `aria-activedescendant`.
 */
export function ListPanel<T>({
  items,
  getKey,
  isDisabled = () => false,
  isSelected,
  renderItem,
  onSelect,
  search,
  initialKey = null,
  multiple = false,
  loading = false,
  message,
  label,
  renderPreview,
}: ListPanelProps<T>) {
  const listId = useId();
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(initialKey);
  const disabledAt = (index: number) => {
    const item = items[index];
    return item === undefined || isDisabled(item);
  };
  // Подсветка хранится по ключу: после фильтрации она остаётся на той же строке или встаёт на первую.
  const found = activeKey === null ? -1 : items.findIndex((item) => getKey(item) === activeKey);
  const activeIndex =
    found >= 0 && !disabledAt(found) ? found : nextEnabledIndex(items.length, -1, 1, disabledAt);
  const optionId = (index: number) => `${listId}-${String(index)}`;

  const virtual = items.length >= VIRTUAL_FROM;
  // eslint-disable-next-line react-hooks/incompatible-library -- React Compiler в сборке не используется: панель перерисовывается целиком
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 36,
    overscan: 8,
    enabled: virtual,
  });

  const activate = (index: number) => {
    const item = items[index];
    if (index < 0 || item === undefined) return;
    setActiveKey(getKey(item));
    if (virtual) virtualizer.scrollToIndex(index, { align: 'auto' });
    else document.getElementById(optionId(index))?.scrollIntoView({ block: 'nearest' });
  };

  // При открытии выбранная строка — посередине видимой части (длинный список времени, городов).
  const revealInitial = useEffectEvent(() => {
    if (activeIndex <= 0) return;
    if (virtual) virtualizer.scrollToIndex(activeIndex, { align: 'center' });
    else document.getElementById(optionId(activeIndex))?.scrollIntoView({ block: 'center' });
  });
  useLayoutEffect(() => {
    revealInitial();
  }, []);

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const inInput = e.currentTarget instanceof HTMLInputElement;
    const moves: Record<string, () => number> = {
      ArrowDown: () => nextEnabledIndex(items.length, activeIndex, 1, disabledAt),
      ArrowUp: () => nextEnabledIndex(items.length, activeIndex, -1, disabledAt),
      PageDown: () => nextEnabledIndex(items.length, activeIndex, PAGE, disabledAt, false),
      PageUp: () => nextEnabledIndex(items.length, activeIndex, -PAGE, disabledAt, false),
      // В строке поиска Home/End двигают курсор, а не подсветку.
      ...(inInput
        ? {}
        : {
            Home: () => nextEnabledIndex(items.length, -1, 1, disabledAt),
            End: () => nextEnabledIndex(items.length, items.length, -1, disabledAt),
          }),
    };
    const move = moves[e.key];
    if (move) {
      e.preventDefault();
      activate(move());
      return;
    }
    const active = items[activeIndex];
    if (e.key === 'Enter' && active !== undefined) {
      e.preventDefault();
      onSelect(active);
    }
  };

  const rows = virtual
    ? virtualizer.getVirtualItems().map((row) => ({ index: row.index, start: row.start }))
    : items.map((_, index) => ({ index, start: 0 }));
  const activeDescendant = activeIndex >= 0 ? optionId(activeIndex) : undefined;

  return (
    <div className="flex min-h-0 min-w-0 flex-col">
      {search ? (
        <div className="border-b border-border-soft p-1 pb-1.5">
          <input
            role="combobox"
            aria-label={search.placeholder ?? t('fields.selectSearch')}
            aria-controls={listId}
            aria-expanded="true"
            aria-autocomplete="list"
            aria-activedescendant={activeDescendant}
            className="h-8 w-full rounded-sm bg-surface-2 px-2.5 text-body outline-none placeholder:text-muted"
            placeholder={search.placeholder ?? t('fields.selectSearch')}
            value={search.value}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => {
              search.onChange(e.target.value);
            }}
            onKeyDown={onKeyDown}
          />
        </div>
      ) : null}
      <div className="flex min-h-0 min-w-0">
        <div
          ref={scrollRef}
          id={listId}
          role="listbox"
          aria-label={label}
          aria-multiselectable={multiple || undefined}
          aria-activedescendant={search ? undefined : activeDescendant}
          tabIndex={search ? -1 : 0}
          onKeyDown={search ? undefined : onKeyDown}
          className="max-h-72 min-h-0 min-w-0 flex-1 overflow-y-auto p-1 outline-none"
        >
          {message ?? (
            <div className="relative" style={virtual ? { height: virtualizer.getTotalSize() } : undefined}>
              {rows.map(({ index, start }) => {
                const item = items[index];
                if (item === undefined) return null;
                const disabled = isDisabled(item);
                const selected = isSelected(item);
                return (
                  <div
                    key={getKey(item)}
                    id={optionId(index)}
                    role="option"
                    aria-selected={selected}
                    aria-disabled={disabled || undefined}
                    data-index={index}
                    ref={virtual ? virtualizer.measureElement : undefined}
                    className={cn('cursor-default select-none', virtual && 'absolute top-0 left-0 w-full')}
                    style={virtual ? { transform: `translateY(${String(start)}px)` } : undefined}
                    onMouseMove={() => {
                      if (!disabled && index !== activeIndex) setActiveKey(getKey(item));
                    }}
                    // Фокус остаётся в строке поиска.
                    onMouseDown={(e) => {
                      e.preventDefault();
                    }}
                    onClick={() => {
                      if (!disabled) onSelect(item);
                    }}
                  >
                    {renderItem(item, { active: index === activeIndex, selected })}
                  </div>
                );
              })}
            </div>
          )}
          {loading ? (
            <div className="flex items-center gap-2 px-2.5 py-2 text-small text-muted">
              <Spinner /> {t('fields.selectLoading')}
            </div>
          ) : null}
        </div>
        {renderPreview && message === undefined && items[activeIndex] !== undefined ? (
          <div className="hidden w-64 shrink-0 border-l border-border-soft p-3 sm:block" aria-hidden="true">
            {renderPreview(items[activeIndex])}
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** Строка варианта: значок, подпись, пояснение; выбранный — галочка (в мультивыборе — флажок слева). */
export function OptionRow({
  label,
  description,
  aside,
  icon,
  active,
  selected,
  disabled = false,
  multiple = false,
}: {
  label: ReactNode;
  description?: ReactNode;
  /** Справа в той же строке: код страны, количество. */
  aside?: ReactNode;
  icon?: ReactNode;
  active: boolean;
  selected: boolean;
  disabled?: boolean | undefined;
  multiple?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex min-h-9 items-center gap-2.5 rounded-md px-2.5 py-1.5 font-semibold [&_svg]:size-4',
        active && 'bg-selected shadow-[inset_3px_0_0_var(--g-accent)]',
        disabled && 'opacity-45',
      )}
    >
      {multiple ? (
        <span
          aria-hidden="true"
          className={cn(
            'grid size-4 shrink-0 place-items-center rounded-xs border-thick border-muted',
            selected && 'border-accent bg-accent text-on-accent',
          )}
        >
          {selected ? <Check className="size-3" strokeWidth={3} /> : null}
        </span>
      ) : null}
      {icon ? <span className="flex shrink-0 text-muted">{icon}</span> : null}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate">{label}</span>
        {description ? (
          <span className="truncate text-caption font-medium text-muted">{description}</span>
        ) : null}
      </span>
      {aside ? (
        <span className="shrink-0 text-small font-medium text-muted tabular-nums">{aside}</span>
      ) : null}
      {!multiple && selected ? <Check className="shrink-0 text-text" aria-hidden="true" /> : null}
    </div>
  );
}

/** Пустой список или ошибка вместо строк. */
export function ListMessage({ children }: { children: ReactNode }) {
  return <div className="px-2.5 py-4 text-center text-small text-muted">{children}</div>;
}
